import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToolConfigService, type ToolConfig, type ToolConfigUpdate } from '../services/toolConfigService';
import type { SetToolAccessRequest, SetToolKindsRequest, ToolKindsPreviewRequest } from '../../../types/backend';

export type ToolConfigMap = Record<string, ToolConfig>;

export const toolsetKeys = {
  configs: () => ['toolset', 'configs'] as const,
  access: (actionId: string) => ['toolset', 'access', actionId] as const,
  stored: () => ['toolset', 'access'] as const,
};

// The persisted admin overlay over the code-defined tool catalog. Best-effort:
// on error the map is empty and the catalog's code defaults render unchanged.
export function useToolConfigs(enabled: boolean) {
  const service = useToolConfigService();

  return useQuery({
    queryKey: toolsetKeys.configs(),
    queryFn: async () => {
      const list = await service!.listToolConfigs();
      return Object.fromEntries(list.map((c) => [c.action_id, c])) as ToolConfigMap;
    },
    enabled: enabled && !!service,
  });
}

export function useUpsertToolConfig() {
  const service = useToolConfigService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, update }: { actionId: string; update: ToolConfigUpdate }) =>
      service!.upsertToolConfig(actionId, update),
    onSuccess: (saved) => {
      queryClient.setQueryData<ToolConfigMap>(toolsetKeys.configs(), (prev) => ({
        ...(prev ?? {}),
        [saved.action_id]: saved,
      }));
    },
  });
}

// The agents a proposed kind mark would take the tool from. A read: nothing is stored.
export function usePreviewToolKinds() {
  const service = useToolConfigService();
  return useMutation({
    mutationFn: (request: ToolKindsPreviewRequest) => service!.previewToolKinds(request),
  });
}

// Store a kind mark; the agents the preview named lose the tool in the same write, and a changed automation tick is
// written on the tool's access, so its access and the stored list are read again.
export function useSetToolKinds() {
  const service = useToolConfigService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SetToolKindsRequest) => service!.setToolKinds(request),
    onSuccess: (saved, request) => {
      queryClient.setQueryData<ToolConfigMap>(toolsetKeys.configs(), (prev) => ({
        ...(prev ?? {}),
        [saved.action_id]: saved,
      }));
      void queryClient.invalidateQueries({ queryKey: toolsetKeys.access(request.action_id), exact: true });
      void queryClient.invalidateQueries({ queryKey: toolsetKeys.stored(), exact: true });
    },
  });
}

// Who may use one tool, as applied now, with its change history (PS-UC-26).
export function useToolAccess(actionId: string, enabled: boolean) {
  const service = useToolConfigService();
  return useQuery({
    queryKey: toolsetKeys.access(actionId),
    queryFn: () => service!.getToolAccess(actionId),
    enabled: enabled && !!service,
  });
}

// Save who may use one tool; the next call anywhere is decided by it.
export function useSetToolAccess() {
  const service = useToolConfigService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SetToolAccessRequest) => service!.setToolAccess(request),
    onSuccess: (view) => {
      queryClient.setQueryData(toolsetKeys.access(view.action_id), view);
      void queryClient.invalidateQueries({ queryKey: toolsetKeys.stored(), exact: true });
    },
  });
}

// Every action with a stored access, as it applies: what the lists and chips show as the applied level.
export function useStoredToolAccess(enabled: boolean) {
  const service = useToolConfigService();
  return useQuery({
    queryKey: toolsetKeys.stored(),
    queryFn: async () => Object.fromEntries((await service!.listToolAccess()).map((a) => [a.action_id, a])),
    enabled: enabled && !!service,
  });
}
